import React from 'react';
import { useHistory } from 'react-router';
import { InPersonSignerPicker, envelopeCanBeSignedInPerson } from '../../common/inPersonSigning';
import type { LaunchDarklyFlags } from '../../common/launchDarkly';
import { checkIfRecipientIsSender } from '../../common/utils';
import { getEnvelopeDocuments, getEnvelopeRecipientsWithTurn } from '../../lib/api/envelope/envelope-api';
import { useDispatch } from '../../lib/reduxHooks';
import { generateFileName } from '../../lib/utils';
import * as actions from '../../store/envelopeHistory/actions';
import { EnvelopeStatus, type IEnvelopeMetadata } from '../../store/envelopeManagement/types';
import { errorToast, hideAlert } from '../../store/pageFrame/actions';
import * as senderActions from '../../store/senderBuild/actions';
import { StyledMenu, StyledMenuItem } from '../common/StyledMenu';
import CancelModal from './CancelModal';
import CorrectModal from './CorrectModal';
import DeleteModal from './DeleteModal';
import RenameModal from './RenameModal';
import ResendModal from './ResendModal';

interface IProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: (e: any) => void;
  envelope: IEnvelopeMetadata;
  sender?: { firstName: string; lastName: string; email: string };
  // Computed once by the parent: this menu renders per table row.
  canSignInPerson?: boolean;
  // Separate from canSignInPerson so Sign Now does not depend on that flag.
  isDelegate?: boolean;
  flags?: LaunchDarklyFlags;
}

// This tracks all of the possible menu options
const menuItemsMap = {
  downloadEnvelope: {
    key: 'downloadEnvelope',
    label: 'Download Envelope',
  },
  downloadCertificate: {
    key: 'downloadCertificate',
    label: 'Download Certificate',
  },
  viewHistory: {
    key: 'viewHistory',
    label: 'View History',
  },
  editEnvelope: {
    key: 'editEnvelope',
    label: 'Edit Envelope',
  },
  resendEnvelope: {
    key: 'resendEnvelope',
    label: 'Remind and Resend ',
  },
  cancelEnvelope: {
    key: 'cancelEnvelope',
    label: 'Cancel Envelope',
  },
  deleteDraft: {
    key: 'deleteDraft',
    label: 'Delete Draft',
  },
  correctEnvelope: {
    key: 'correctEnvelope',
    label: 'Correct Envelope',
  },
  signNow: {
    key: 'signNow',
    label: 'Sign Envelope',
  },
  signInPerson: {
    key: 'signInPerson',
    label: 'Sign In-Person',
  },
  renameEnvelope: {
    key: 'renameEnvelope',
    label: 'Edit Envelope Name',
  },
};

// This tracks what options we want for each envelope status
// This is the order actions should be in if they are being included:
// signEnvelope
// renameEnvelope
// editEnvelope
// resendEnvelope
// downloadEnvelope
// downloadCertificate
// viewHistory
// cancelEnvelope
export const envelopeStatusActionsMap = {
  [EnvelopeStatus.DRAFT]: [menuItemsMap.deleteDraft],
  [EnvelopeStatus.COMPLETED]: [
    menuItemsMap.downloadEnvelope,
    menuItemsMap.downloadCertificate,
    menuItemsMap.viewHistory,
  ],
  [EnvelopeStatus.SENT]: [
    menuItemsMap.correctEnvelope,
    menuItemsMap.resendEnvelope,
    menuItemsMap.downloadEnvelope,
    menuItemsMap.downloadCertificate,
    menuItemsMap.viewHistory,
    menuItemsMap.cancelEnvelope,
  ],
  [EnvelopeStatus.INPROGRESS]: [
    menuItemsMap.correctEnvelope,
    menuItemsMap.resendEnvelope,
    menuItemsMap.downloadEnvelope,
    menuItemsMap.downloadCertificate,
    menuItemsMap.viewHistory,
    menuItemsMap.cancelEnvelope,
  ],
  [EnvelopeStatus.CANCELED]: [menuItemsMap.downloadEnvelope, menuItemsMap.viewHistory],
  [EnvelopeStatus.CORRECTING]: [menuItemsMap.downloadEnvelope, menuItemsMap.viewHistory, menuItemsMap.cancelEnvelope],
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const ActionsMenu = (props: IProps) => {
  const { open, anchorEl, onClose, envelope, sender } = props;
  const showEditEnvelopeName = props.flags && props.flags['show-edit-envelope-name'];
  const history = useHistory();
  const dispatch = useDispatch();
  const [showCancelModal, setShowCancelModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showResendModal, setShowResendModal] = React.useState(false);
  const [showCorrectModal, setShowCorrectModal] = React.useState(false);
  const [showRenameModal, setShowRenameModal] = React.useState(false);
  const [showSignerPicker, setShowSignerPicker] = React.useState(false);
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const openResendModal = () => {
    setShowResendModal(true);
    dispatch(actions.resend(envelope.id));
  };

  // Resolved for this envelope rather than read off senderBuild.recipientsModal,
  // which one slot serves to every row's menu and whose fetch carries no envelope
  // id. A quick click could otherwise mint against the last envelope's signer.
  async function resolveSenderSignerId(): Promise<string | undefined> {
    const { recipients } = await getEnvelopeRecipientsWithTurn(envelope.id);
    return recipients?.find(
      (r) => checkIfRecipientIsSender(r) && r.email?.toLowerCase() === sender?.email?.toLowerCase()
    )?.id;
  }

  async function signAsSender() {
    let resolved: string | undefined;
    try {
      resolved = await resolveSenderSignerId();
    } catch {
      // Minting against an unknown signer only fails later and less clearly.
      dispatch(errorToast('We could not load the signers for this envelope. Please try again.'));
      return undefined;
    }
    if (!resolved) {
      // senderCanSign said there was a row for them, so the list disagreeing
      // with it is worth saying out loud rather than sending a token request
      // that cannot succeed.
      dispatch(errorToast('We could not find your signer row on this envelope. Please refresh and try again.'));
      return undefined;
    }
    return dispatch(
      senderActions.fetchSignerToken({
        envelopeId: envelope.id,
        accountId: envelope.accountId,

        signerId: resolved,

        // @ts-expect-error -- This code was written prior to enforcing lint rules
        signerEmail: sender?.email,
      })
    );
  }

  // This tracks what actions we want to occur on the click of an option
  const menuItemsActionsMap = {
    viewHistory: () => history.push(`/envelopes/${envelope.id}/history`),
    editEnvelope: () => history.push(`/envelopes/${envelope.id}/documents`),
    cancelEnvelope: () => setShowCancelModal(true),
    resendEnvelope: () => openResendModal(),
    downloadEnvelope: async () => {
      const docs = await getEnvelopeDocuments(envelope.id);
      const envelopeFileName = generateFileName(docs);
      dispatch(actions.downloadEnvelope(envelope.id, envelopeFileName, false));
    },
    downloadCertificate: () => {
      const certificateFileName = 'DigiSignAuditCertificate.pdf';
      dispatch(actions.downloadCertificate(envelope.id, certificateFileName, false));
    },
    deleteDraft: () => setShowDeleteModal(true),
    correctEnvelope: () => setShowCorrectModal(true),
    signNow: () => signAsSender(),
    signInPerson: () => setShowSignerPicker(true),
    renameEnvelope: () => setShowRenameModal(true),
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const clickMenuItem = async (event: any, itemKey?: string) => {
    event.stopPropagation();
    try {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      await menuItemsActionsMap[itemKey || '']();
    } finally {
      // Several of these await a request. A rejection used to skip onClose and
      // leave the menu sitting open over a dashboard that had not changed.
      onClose(event);
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const clickHandler = () => {
    setShowResendModal(false);
    dispatch(hideAlert());
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const getActionItems = () => {
    // Sign Now: the owner signing their own row, so they must be a signer.
    // Explicitly false, not merely falsy: isDelegate is undefined until user
    // info resolves, and treating that as the owner flashes this at a delegate.
    const isOwner = props.isDelegate === false;
    const addSignNow =
      isOwner && envelope.senderCanSign && (envelope.status === 'Sent' || envelope.status === 'InProgress');
    // Sign In-Person only needs somebody left to sign; the service decides
    // whose turn it is.
    const addSignInPerson = props.canSignInPerson && envelopeCanBeSignedInPerson(envelope);

    // Order matches the design.
    const signingOptions = [
      ...(addSignNow ? [menuItemsMap.signNow] : []),
      ...(addSignInPerson ? [menuItemsMap.signInPerson] : []),
    ];

    // @ts-expect-error -- This code was written prior to enforcing lint rules
    const statusOptions = envelopeStatusActionsMap[envelope.status.toLowerCase()];

    if (showEditEnvelopeName) {
      return signingOptions.concat(menuItemsMap.renameEnvelope, statusOptions);
    }
    return signingOptions.concat(statusOptions);
  };

  return (
    <>
      <StyledMenu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        keepMounted
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        data-spec="actions-menu"
      >
        {getActionItems().map((item: { key: string; label: string; action?: () => void }) => (
          <StyledMenuItem
            data-spec={`actions-menu-item-${item.key}`}
            onClick={async (e) => clickMenuItem(e, item.key)}
            key={item.key}
          >
            {item.label}
          </StyledMenuItem>
        ))}
      </StyledMenu>
      {showDeleteModal && <DeleteModal envelopeId={envelope.id} close={() => setShowDeleteModal(false)} />}
      {showCancelModal && <CancelModal envelopeId={envelope.id} close={() => setShowCancelModal(false)} />}
      {/* @ts-expect-error -- This code was written prior to enforcing lint rules */}
      {showResendModal && <ResendModal close={clickHandler} flags={props.flags!} />}
      {showCorrectModal && <CorrectModal envelopeId={envelope.id} close={() => setShowCorrectModal(false)} />}
      {showRenameModal && (
        <RenameModal envelopeId={envelope.id} envelopeName={envelope.name} close={() => setShowRenameModal(false)} />
      )}
      {showSignerPicker && (
        <InPersonSignerPicker
          envelopeId={envelope.id}
          senderEmail={sender?.email}
          onClose={() => setShowSignerPicker(false)}
        />
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default ActionsMenu;
